import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Observable, combineLatest, filter, map, of, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CustomClaimService } from '../services/custom-claim.service';
import { MembershipRoles } from '../data-models/membership-roles.model';

/**
 * Route data shape for `MembershipRoleGuard`.
 *
 * ```ts
 * { path: 'work-orders', data: { membershipRoles: [MembershipRoles.Owner, MembershipRoles.ChiefSupervisor] } }
 * ```
 *
 * The guard treats the array as "any of" — the user is allowed in when
 * any one of the listed roles is set on their membership in the
 * currently-active org.
 */
export interface MembershipRoleRouteData {
  membershipRoles: MembershipRoles[];
  redirectTo?: string;
}

/**
 * Verifies that the signed-in user has at least one of the listed
 * `MembershipRoles`s in the active organization. Used to gate org-scoped
 * admin features (Work Orders module et al) to Owners + Chief
 * Supervisors.
 *
 * Note: this is an *org-scoped* check, distinct from `ClaimGuard`
 * which only sees the global-claims surface (Admin / SuperAdmin).
 */
@Injectable({ providedIn: 'root' })
export class MembershipRoleGuard {
  constructor(
    private authService: AuthService,
    private customClaimService: CustomClaimService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const data = route.data['membershipRoles'] as
      | MembershipRoles[]
      | MembershipRoleRouteData
      | undefined;
    if (!data) return of(true);

    const roles: MembershipRoles[] = Array.isArray(data) ? data : data.membershipRoles;
    const redirectTo =
      !Array.isArray(data) && data.redirectTo ? data.redirectTo : '/dashboard';
    if (!roles || roles.length === 0) return of(true);

    return combineLatest([
      this.authService.loading$,
      this.authService.currentUser$,
    ]).pipe(
      filter(([loading]) => !loading),
      take(1),
      map(([_, user]) => {
        if (!user) {
          return this.router.createUrlTree(['/login']);
        }
        if (this.customClaimService.hasAnyOrgRole(roles)) {
          return true;
        }
        return this.router.createUrlTree([redirectTo]);
      }),
    );
  }
}
